/**
 * <s-parking-map> - Lightweight, dependency-free street map for the "parkeren" section.
 * Draws greyscale OpenStreetMap tiles for a fixed area, then overlays (from a child
 * <script type="application/json" data-map-config>):
 *   - store: { lat, lon, label }            black pill label with a dot
 *   - parkings: [{ id, label, polygons: [[[lat, lon], ...]], pins: [[lat, lon]], walk: [[lat, lon], ...] }] (polygon/pin also accepted)
 *     polygon = filled violet area, pin = position of the "P" badge, walk = dotted walking route
 * Zoom buttons (data-zoom-in / data-zoom-out) step between minZoom and maxZoom.
 * Elements with [data-parking-card="<id>"] highlight their parking on hover/focus.
 * Overlays draw on connect; tile images load lazily.
 */
const TILE_SIZE = 256;
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const SVG_NS = 'http://www.w3.org/2000/svg';

function project(lat, lon, zoom) {
  const scale = TILE_SIZE * 2 ** zoom;
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((lon + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

class SParkingMap extends HTMLElement {
  connectedCallback() {
    const configNode = this.querySelector('[data-map-config]');
    if (!configNode) return;
    try {
      this.config = JSON.parse(configNode.textContent);
    } catch {
      return;
    }
    this.viewport = this.querySelector('[data-map-viewport]');
    if (!this.viewport) return;
    this.zoom = this.config.zoom || 18;
    this.minZoom = this.config.minZoom || 16;
    this.maxZoom = this.config.maxZoom || 19;

    this.querySelector('[data-zoom-in]')?.addEventListener('click', () => this.setZoom(this.zoom + 1));
    this.querySelector('[data-zoom-out]')?.addEventListener('click', () => this.setZoom(this.zoom - 1));

    for (const card of document.querySelectorAll('[data-parking-card]')) {
      const id = card.dataset.parkingCard;
      card.addEventListener('mouseenter', () => this.highlight(id));
      card.addEventListener('mouseleave', () => this.highlight(null));
      card.addEventListener('focusin', () => this.highlight(id));
      card.addEventListener('focusout', () => this.highlight(null));
    }

    this.started = true;
    this.render();
    this.resizeObserver = new ResizeObserver(() => this.render());
    this.resizeObserver.observe(this.viewport);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  setZoom(zoom) {
    const next = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
    if (next === this.zoom) return;
    this.zoom = next;
    this.userZoomed = true;
    if (this.started) this.render();
    this.updateZoomButtons();
  }

  updateZoomButtons() {
    const zoomIn = this.querySelector('[data-zoom-in]');
    const zoomOut = this.querySelector('[data-zoom-out]');
    if (zoomIn) zoomIn.disabled = this.zoom >= this.maxZoom;
    if (zoomOut) zoomOut.disabled = this.zoom <= this.minZoom;
  }

  highlight(id) {
    for (const node of this.viewport.querySelectorAll('[data-parking]')) {
      const active = id === null || node.dataset.parking === id;
      node.style.opacity = active ? '1' : '0.35';
    }
  }

  // A parking can have one area (polygon/pin) or several (polygons/pins).
  polygonsOf(parking) {
    return parking.polygons || (parking.polygon ? [parking.polygon] : []);
  }

  pinsOf(parking) {
    return parking.pins || (parking.pin ? [parking.pin] : []);
  }

  allPoints() {
    const { store, parkings } = this.config;
    const points = [[store.lat, store.lon]];
    for (const parking of parkings) points.push(...this.polygonsOf(parking).flat(), ...(parking.walk || []));
    return points;
  }

  render() {
    const width = this.viewport.clientWidth;
    const height = this.viewport.clientHeight;
    if (!width || !height) return;
    // Until the visitor zooms, pick the closest zoom at which everything fits with some padding.
    if (!this.userZoomed) {
      const padding = Math.min(64, Math.round(Math.min(width, height) * 0.12));
      const pts = this.allPoints().map(([lat, lon]) => project(lat, lon, 0));
      const spanX = Math.max(...pts.map((p) => p.x)) - Math.min(...pts.map((p) => p.x));
      const spanY = Math.max(...pts.map((p) => p.y)) - Math.min(...pts.map((p) => p.y));
      const fit = Math.log2(Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY));
      // Round down to a quarter step so tiles stay crisp and everything still fits.
      this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, Math.floor(fit * 4) / 4));
      this.updateZoomButtons();
    }
    const zoom = this.zoom;

    // Centre on the middle of everything we draw.
    const projected = this.allPoints().map(([lat, lon]) => project(lat, lon, zoom));
    const xs = projected.map((p) => p.x);
    const ys = projected.map((p) => p.y);
    const centre = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
    const originX = centre.x - width / 2;
    const originY = centre.y - height / 2;
    const toScreen = (lat, lon) => {
      const p = project(lat, lon, zoom);
      return { x: p.x - originX, y: p.y - originY };
    };

    this.viewport.replaceChildren();

    // Tiles
    const tiles = document.createElement('div');
    tiles.className = 'parking-map__tiles';
    tiles.setAttribute('aria-hidden', 'true');
    // Zoom can be fractional: load the next whole tile level and scale those tiles down to fit.
    const tileZoom = Math.min(19, Math.ceil(zoom - 0.001));
    const tileScale = 2 ** (zoom - tileZoom);
    const shownSize = TILE_SIZE * tileScale;
    const maxTile = 2 ** tileZoom;
    const firstX = Math.floor(originX / shownSize);
    const firstY = Math.floor(originY / shownSize);
    const lastX = Math.floor((originX + width) / shownSize);
    const lastY = Math.floor((originY + height) / shownSize);
    for (let tx = firstX; tx <= lastX; tx++) {
      for (let ty = firstY; ty <= lastY; ty++) {
        if (ty < 0 || ty >= maxTile) continue;
        const img = document.createElement('img');
        img.src = TILE_URL.replace('{z}', tileZoom).replace('{x}', ((tx % maxTile) + maxTile) % maxTile).replace('{y}', ty);
        img.alt = '';
        img.width = TILE_SIZE;
        img.height = TILE_SIZE;
        img.decoding = 'async';
        img.loading = 'lazy';
        img.draggable = false;
        img.style.cssText = `position:absolute;left:${tx * shownSize - originX}px;top:${ty * shownSize - originY}px;width:${shownSize + 0.5}px;height:${shownSize + 0.5}px;max-width:none;`;
        tiles.append(img);
      }
    }
    this.viewport.append(tiles);

    // Vector overlay: parking areas and dotted walking routes
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    svg.setAttribute('aria-hidden', 'true');
    svg.style.cssText = 'position:absolute;inset:0;overflow:visible;';
    const lineWidth = Math.max(2, zoom - 15);
    for (const parking of this.config.parkings) {
      const group = document.createElementNS(SVG_NS, 'g');
      group.dataset.parking = parking.id;
      group.style.transition = 'opacity 200ms ease';
      for (const polygon of this.polygonsOf(parking)) {
        const area = document.createElementNS(SVG_NS, 'polygon');
        area.setAttribute('points', polygon.map(([lat, lon]) => {
          const p = toScreen(lat, lon);
          return `${p.x},${p.y}`;
        }).join(' '));
        area.setAttribute('fill', 'var(--color-accent, #7e44ec)');
        area.setAttribute('fill-opacity', '0.85');
        area.setAttribute('stroke', 'var(--color-accent-hover, #5c2db8)');
        area.setAttribute('stroke-width', '1.5');
        area.setAttribute('stroke-linejoin', 'round');
        group.append(area);
      }
      if (parking.walk?.length) {
        const walk = document.createElementNS(SVG_NS, 'polyline');
        walk.setAttribute('points', parking.walk.map(([lat, lon]) => {
          const p = toScreen(lat, lon);
          return `${p.x},${p.y}`;
        }).join(' '));
        walk.setAttribute('fill', 'none');
        walk.setAttribute('stroke', 'var(--color-on-surface, #0c0c0c)');
        walk.setAttribute('stroke-width', lineWidth);
        walk.setAttribute('stroke-linecap', 'round');
        walk.setAttribute('stroke-linejoin', 'round');
        walk.setAttribute('stroke-dasharray', `0.1 ${lineWidth * 2.6}`);
        group.append(walk);
      }
      svg.append(group);
    }
    this.viewport.append(svg);

    // "P" badges
    for (const parking of this.config.parkings) {
      for (const pin of this.pinsOf(parking)) {
        const p = toScreen(...pin);
        const badge = document.createElement('span');
        badge.className = 'parking-map__pin';
        badge.dataset.parking = parking.id;
        badge.textContent = 'P';
        badge.title = parking.label;
        badge.style.cssText = `left:${p.x}px;top:${p.y}px;`;
        this.viewport.append(badge);
      }
    }

    // Store label with dot
    const store = toScreen(this.config.store.lat, this.config.store.lon);
    const dot = document.createElement('span');
    dot.className = 'parking-map__store-dot';
    dot.style.cssText = `left:${store.x}px;top:${store.y}px;`;
    const label = document.createElement('span');
    label.className = 'parking-map__store-label';
    if (this.config.store.logo) {
      const logo = document.createElement('img');
      logo.src = this.config.store.logo;
      logo.alt = this.config.store.label;
      logo.className = 'parking-map__store-logo';
      label.append(logo);
    } else {
      label.textContent = this.config.store.label;
    }
    label.style.cssText = `left:${store.x}px;top:${store.y}px;`;
    this.viewport.append(dot, label);
  }
}

if (!customElements.get('s-parking-map')) customElements.define('s-parking-map', SParkingMap);
